import React from 'react';

import type { Paths } from '@zg-rentals/paths-manifest';

// styles
import { StyledFixedAdminBar, StyledPostingPathAdminText, StyledWrapper } from './style';
import { Anchor, Text } from '@zillow/constellation';
import type { Brand } from '../../interfaces/brand';
import type { User } from '../../interfaces/user';

const USER_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://rental-manager-api.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

const LISTING_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://listing-tools.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

interface AdminNavProps {
  brand: Brand | string;
  userModel: User | null;
  zgApi?: string;
  paths: Paths;
  signInPage: string;
  isDevEnv?: boolean;
  listingId?: string;
  handleStopImpersonatingClick?(event?: Event): Promise<void> | void;
  handleImpersonateClick?(event?: Event): Promise<void> | void;
}

export default function AdminNav(props: AdminNavProps) {
  async function handleStopImpersonatingClick(event: Event) {
    event.preventDefault();
    if (props.handleStopImpersonatingClick) {
      await props.handleStopImpersonatingClick();
    }
    if (props.paths.stopImpersonatingPage) {
      window.location.assign(props.paths.stopImpersonatingPage);
    }
  }
  async function handleImpersonateClick(event: Event) {
    event.preventDefault();

    if (props.handleImpersonateClick) {
      await props.handleImpersonateClick();
    }
    window.location.assign(props.paths.impersonatePage);
  }
  const { userModel, brand, zgApi, signInPage, listingId, isDevEnv } = props;
  const { email, impersonatorEmail, impersonatorUserToken, loggedIn, isPostingPathAdmin } = userModel ?? {};
  const isImpersonator = Boolean(impersonatorEmail && impersonatorUserToken && isPostingPathAdmin);
  const userInspectorHostName = isDevEnv ? USER_INSPECTOR_HOST_NAME.COMET1 : USER_INSPECTOR_HOST_NAME.PRODUCTION;
  const listingInspectorHostName = isDevEnv
    ? LISTING_INSPECTOR_HOST_NAME.COMET1
    : LISTING_INSPECTOR_HOST_NAME.PRODUCTION;
  const inspectUserUrl =
    userInspectorHostName +
    '/rental-manager-api/inspectorTool/userInspectorTool?zgBrand=' +
    brand +
    '&userIdentifier=' +
    email +
    '&submitAction=inspectUser';

  if (zgApi) {
    // eslint-disable-next-line no-console
    console.warn('zgApi is deprecated and will be removed in the next major release.');
  }

  if (!isPostingPathAdmin) {
    return null;
  }
  // If session expired from 30 minutes of inactivity, while impersonating, we need
  // to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this on __PRODUCTION__
  if (isImpersonator && process.env.NODE_ENV === 'production' && !loggedIn) {
    window.location.assign(signInPage);
  }

  let inspectListingUrl;
  if (!listingId) {
    inspectListingUrl = '';
  } else {
    let listingIdQueryParams;
    if (listingId.includes('_')) {
      const feedListingIdAndFeedId = listingId.split('_');
      listingIdQueryParams = 'feedId=' + feedListingIdAndFeedId[1] + '&feedListingId=' + feedListingIdAndFeedId[0];
    } else {
      listingIdQueryParams = 'aliasEncoded=' + listingId;
    }
    inspectListingUrl =
      listingInspectorHostName +
      '/listing-tools/listing/listingInspectorTool?' +
      listingIdQueryParams +
      '&submitAction=inspectListingFromDatabase';
  }

  return (
    <StyledWrapper isImpersonator={isImpersonator} data-testid="AdminNav">
      <StyledFixedAdminBar isImpersonator={isImpersonator}>
        <StyledPostingPathAdminText
          fontType="bodySmallHeading"
          isImpersonator={isImpersonator}
          id="PostingPathAdminText"
          data-testid="PostingPathAdminText"
        >
          {isImpersonator ? 'Impersonating' : 'PostingPathAdmin'}
        </StyledPostingPathAdminText>

        <Text fontType="bodySmall">
          <Anchor href={inspectUserUrl} target="_blank" id="Email" data-testid="Email">
            {email}
          </Anchor>

          <span> | </span>
        </Text>

        {listingId && (
          <Text fontType="bodySmall">
            <Anchor href={inspectListingUrl} target="_blank">
              {' Inspect Listing '}
            </Anchor>
            <span> | </span>
          </Text>
        )}
        {isImpersonator ? (
          <Text fontType="bodySmall">
            <Anchor
              id="StopImpersonating"
              data-testid="StopImpersonating"
              // @ts-ignore
              onClick={handleStopImpersonatingClick}
            >
              Stop Impersonating
            </Anchor>
          </Text>
        ) : (
          <Text fontType="bodySmall">
            <Anchor
              // @ts-ignore
              onClick={handleImpersonateClick}
            >
              Impersonate User
            </Anchor>
          </Text>
        )}
      </StyledFixedAdminBar>
    </StyledWrapper>
  );
}
